// Centralized Error Handler - Standardizes error handling across the application
// ✅ NO MAGIC STRINGS: Use typed enums and constants

import { EventBus } from "../events/EventBus";
import debugLogger from "../utils/debugLogger";

import {
  ErrorSeverity,
  ErrorSource,
  MAX_HISTORY_SIZE,
} from "./error-constants";
import {
  buildApiErrorOptions,
  buildAuthErrorOptions,
  buildCreateErrorOptions,
  buildErrorNotification,
  buildValidationErrorOptions,
  createEmptyErrorsBySeverity,
  createEmptyErrorsBySource,
  emitErrorNotification,
  emitErrorOccurredEvent,
  emitErrorRetryRequested,
  logAppError,
  normalizeError,
  reportErrorToAnalytics,
  setupErrorEventHandlers,
} from "./error-helpers";
import type {
  AppError,
  ErrorHandlerOptions,
  ErrorNotification,
} from "./error-types";

// Re-export public types and enums for backwards-compatible imports
export { ErrorSeverity, ErrorSource } from "./error-constants";
export type {
  AppError,
  ErrorContext,
  ErrorDismissedPayload,
  ErrorHandlerOptions,
  ErrorNotification,
  ErrorRetryRequestedPayload,
} from "./error-types";

/**
 * Centralized Error Handler Class
 */
export class ErrorHandler {
  private eventBus: EventBus;
  private errorCount = 0;
  private errorHistory: AppError[] = [];
  private maxHistorySize = MAX_HISTORY_SIZE;

  constructor(eventBus: EventBus) {
    this.eventBus = eventBus;
    setupErrorEventHandlers(this.eventBus);
    debugLogger.info("error-handler", "Centralized Error Handler initialized");
  }

  /**
   * Handle any error with standardized processing
   */
  public handleError(
    error: unknown,
    options: ErrorHandlerOptions = {},
  ): AppError {
    const {
      showNotification = true,
      logError = true,
      reportToAnalytics = true,
      retryable = false,
      userFriendlyMessage,
      severity,
      source,
      context = {},
    } = options;

    const appError = normalizeError(error, {
      retryable,
      userFriendlyMessage,
      severity,
      source,
      context,
    });

    this.addToHistory(appError);

    if (logError) {
      logAppError(appError);
    }

    if (showNotification) {
      this.showNotification(appError);
    }

    if (reportToAnalytics) {
      reportErrorToAnalytics(this.eventBus, appError);
    }

    emitErrorOccurredEvent(this.eventBus, appError);

    return appError;
  }

  /**
   * Create a new error with context
   */
  public createError(
    message: string,
    source: ErrorSource = ErrorSource.APPLICATION,
    severity: ErrorSeverity = ErrorSeverity.ERROR,
    options: ErrorHandlerOptions = {},
  ): AppError {
    const error = new Error(message);
    return this.handleError(error, {
      ...buildCreateErrorOptions(severity, options),
      source,
    });
  }

  /**
   * Handle API errors specifically
   */
  public handleApiError(
    error: unknown,
    endpoint?: string,
    options: ErrorHandlerOptions = {},
  ): AppError {
    return this.handleError(error, buildApiErrorOptions(endpoint, options));
  }

  /**
   * Handle validation errors specifically
   */
  public handleValidationError(
    error: unknown,
    field?: string,
    options: ErrorHandlerOptions = {},
  ): AppError {
    return this.handleError(error, buildValidationErrorOptions(field, options));
  }

  /**
   * Handle authentication errors specifically
   */
  public handleAuthError(
    error: unknown,
    action?: string,
    options: ErrorHandlerOptions = {},
  ): AppError {
    return this.handleError(error, buildAuthErrorOptions(action, options));
  }

  /**
   * Get error statistics
   */
  public getErrorStats(): {
    totalErrors: number;
    errorsBySeverity: Record<ErrorSeverity, number>;
    errorsBySource: Record<ErrorSource, number>;
    recentErrors: AppError[];
  } {
    const errorsBySeverity = createEmptyErrorsBySeverity();
    const errorsBySource = createEmptyErrorsBySource();

    this.errorHistory.forEach((error) => {
      errorsBySeverity[error.severity]++;
      errorsBySource[error.source]++;
    });

    return {
      totalErrors: this.errorCount,
      errorsBySeverity,
      errorsBySource,
      recentErrors: this.errorHistory.slice(-10), // Last 10 errors
    };
  }

  /**
   * Clear error history
   */
  public clearErrorHistory(): void {
    this.errorHistory = [];
    this.errorCount = 0;
    debugLogger.info("error-handler", "Error history cleared");
  }

  /**
   * Get errors by severity
   */
  public getErrorsBySeverity(severity: ErrorSeverity): AppError[] {
    return this.errorHistory.filter((error) => error.severity === severity);
  }

  /**
   * Get errors by source
   */
  public getErrorsBySource(source: ErrorSource): AppError[] {
    return this.errorHistory.filter((error) => error.source === source);
  }

  /**
   * Check if an error is retryable
   */
  public isRetryableError(error: AppError): boolean {
    return error.retryable;
  }

  /**
   * Add error to history
   */
  private addToHistory(error: AppError): void {
    this.errorHistory.push(error);
    this.errorCount++;

    if (this.errorHistory.length > this.maxHistorySize) {
      this.errorHistory.shift();
    }
  }

  /**
   * Show error notification
   */
  private showNotification(error: AppError): void {
    const notification: ErrorNotification = buildErrorNotification(error, () =>
      emitErrorRetryRequested(this.eventBus, error),
    );
    emitErrorNotification(this.eventBus, notification);
  }
}

/**
 * Error Handler factory function
 */
export function createErrorHandler(eventBus: EventBus): ErrorHandler {
  return new ErrorHandler(eventBus);
}
